import React from "react";
import styled from "styled-components";
import {
  MODE,
  coverText,
  formatBasketMoney,
  minimumProgressOf,
  removedLineText,
  shareStatusLabel,
  shareStatusTone,
} from "../../utils/groupBasketView";
import {
  Card,
  CardTitle,
  Divider,
  GREEN,
  GREEN_SOFT,
  LINE,
  MUTED,
  MoneyRow,
  Muted,
  Notice,
  Pill,
  SectionLabel,
  TEXT,
} from "./groupBasketStyles";

const FEE_UNAVAILABLE_TEXT = {
  freight_quoted_separately: "Quoted separately",
  quote_failed: "Not available yet",
};

/*
 * The basket's money, exactly as the server sent it: before lock an estimate
 * (`pricing`), from lock on the fixed figures (`lock`, `shares`,
 * `collection`, `remainder`). Nothing here is added up.
 */
const GroupBasketMoney = ({ basket }) => {
  const currency = basket?.currency;
  const money = (amount) => formatBasketMoney(amount, currency);
  const pricing = basket?.pricing || null;
  const lock = basket?.lock || null;
  const shares = basket?.shares || [];
  const collection = basket?.collection || null;
  const remainder = basket?.remainder || null;
  const figures = lock || pricing;
  if (!figures) {
    return (
      <Card aria-label="Basket total">
        <CardTitle>Basket so far</CardTitle>
        <Muted>The total shows here once items are added.</Muted>
      </Card>
    );
  }
  const estimate = pricing?.isEstimate === true;
  const minimum = minimumProgressOf(pricing);

  return (
    <Card aria-label="Basket total">
      <HeadRow>
        <CardTitle>{lock ? "Order total" : "Basket so far"}</CardTitle>
        {estimate && <Pill $tone="wait">Estimate</Pill>}
      </HeadRow>
      {estimate && (
        <Muted>Prices are checked again when the basket locks. Shares are fixed then.</Muted>
      )}

      <Figures>
        <MoneyRow>
          <span>Items</span>
          <b>{money(figures.itemsSubtotal)}</b>
        </MoneyRow>
        <MoneyRow>
          <span>Delivery{estimate ? " (estimate)" : ""}</span>
          {figures.deliveryFee === null || figures.deliveryFee === undefined ? (
            <span>{FEE_UNAVAILABLE_TEXT[pricing?.deliveryFeeUnavailableReason] || "Not available yet"}</span>
          ) : (
            <b>{money(figures.deliveryFee)}</b>
          )}
        </MoneyRow>
        {basket?.mode === MODE.EACH_PAYS_SHARE && !lock && pricing?.feeSharePerMember != null && (
          <MoneyRow>
            <span>
              Delivery per person
              {pricing.membersWithItems ? ` (${pricing.membersWithItems} with items)` : ""}
            </span>
            <b>{money(pricing.feeSharePerMember)}</b>
          </MoneyRow>
        )}
        <Divider />
        <MoneyRow $strong>
          <span>{lock ? "Total" : "Total so far"}</span>
          <b>{money(figures.total)}</b>
        </MoneyRow>
      </Figures>

      {!lock && minimum && (
        <Minimum>
          <MoneyRow>
            <span>Minimum order</span>
            <b>{money(minimum.minimum)}</b>
          </MoneyRow>
          <Bar role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(minimum.fill * 100)}>
            <span style={{ width: `${Math.round(minimum.fill * 100)}%` }} />
          </Bar>
          <Muted>
            {minimum.met
              ? "Minimum reached."
              : `Add ${money(minimum.shortfall)} more to reach the minimum.`}
          </Muted>
        </Minimum>
      )}

      {lock?.removedLines?.length > 0 && (
        <Notice $tone="wait" style={{ marginTop: 14 }}>
          <span>
            <strong>Removed when the basket locked: </strong>
            {lock.removedLines.map(removedLineText).join("; ")}
            .
          </span>
        </Notice>
      )}

      {shares.length > 0 && (
        <>
          <Divider />
          <SectionLabel>Shares</SectionLabel>
          <ShareList>
            {shares.map((share) => (
              <ShareRow key={share.memberId}>
                <div>
                  <strong>
                    {share.label}
                    {share.isHost ? " (host)" : ""}
                  </strong>
                  <small>
                    Items {money(share.goods)} + delivery {money(share.feeShare)}
                    {Number(share.rounding) ? ` + rounding ${money(share.rounding)}` : ""}
                  </small>
                  {share.lastFailureReason && share.status === "UNPAID" ? (
                    <small className="fail">Last attempt: {share.lastFailureReason}</small>
                  ) : null}
                </div>
                <div className="side">
                  <b>{money(share.amount)}</b>
                  <Pill $tone={shareStatusTone(share.status)}>{shareStatusLabel(share.status)}</Pill>
                </div>
              </ShareRow>
            ))}
          </ShareList>
        </>
      )}

      {collection && shares.length > 0 && (
        <>
          <Divider />
          <MoneyRow>
            <span>Paid</span>
            <b>{money(collection.settled)}</b>
          </MoneyRow>
          {Number(collection.pending) > 0 && (
            <MoneyRow>
              <span>Confirming</span>
              <b>{money(collection.pending)}</b>
            </MoneyRow>
          )}
          {basket?.state !== "CANCELLED" && (
            <MoneyRow $strong>
              <span>Still to pay</span>
              <b>{money(collection.outstanding)}</b>
            </MoneyRow>
          )}
        </>
      )}

      {remainder && basket?.state !== "CANCELLED" && (
        <Remainder>
          <MoneyRow $strong>
            <span>Left for the host to pay</span>
            <b>{money(remainder.amount)}</b>
          </MoneyRow>
          {remainder.covers?.length > 0 && (
            <Muted>
              Covers{" "}
              {remainder.covers.map(coverText(money)).join(", ")}
            </Muted>
          )}
          <div>
            <Pill $tone={shareStatusTone(remainder.status)}>{shareStatusLabel(remainder.status)}</Pill>
          </div>
        </Remainder>
      )}
    </Card>
  );
};

const HeadRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const Figures = styled.div`
  margin-top: 10px;
`;

const Minimum = styled.div`
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 14px;
  background: #f7faf4;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Bar = styled.div`
  height: 8px;
  border-radius: 999px;
  background: ${GREEN_SOFT};
  overflow: hidden;

  span {
    display: block;
    height: 100%;
    border-radius: 999px;
    background: ${GREEN};
    transition: width 300ms ease;
  }
`;

const ShareList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const ShareRow = styled.li`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;

  & + & {
    border-top: 1px solid ${LINE};
  }

  strong {
    display: block;
    font-size: 15px;
    font-weight: 800;
    color: ${TEXT};
  }

  small {
    display: block;
    margin-top: 2px;
    font-size: 13px;
    color: ${MUTED};
  }

  small.fail {
    color: #8a5a00;
    font-weight: 700;
  }

  .side {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
    flex-shrink: 0;
  }

  b {
    font-size: 15px;
    font-weight: 800;
    color: ${TEXT};
    font-variant-numeric: tabular-nums;
  }
`;

const Remainder = styled.div`
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 14px;
  background: #f7faf4;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export default GroupBasketMoney;
